'use client'

import { AnimatePresence, motion } from 'motion/react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { createContext, Fragment, useCallback, useContext, useState, type MouseEvent, type ReactNode } from 'react'

import { organizationStatusMeta } from '@components/app/organization-status'
import { StatusDot } from '@components/ui/card'
import { Combobox } from '@components/ui/combobox'
import { cn } from '@lib/cn'
import { ENVIRONMENT_LABELS, switchEnvironment } from '@lib/environment'
import { ENVIRONMENT_KINDS, useAppRoute } from '@lib/use-app-route'
import { useOrganizations } from '@resolvers/organizations'
import { useWorkosSummary } from '@resolvers/workos'

import TeamMenu from './team-menu'
import { isNavActive, organizationPath, useSidebarNav, type SidebarChild, type SidebarItem } from './use-sidebar-nav'
import UserMenu from './user-menu'

const navigateContext = createContext<(href: string) => void>(() => {})

const isPlainClick = (event: MouseEvent) => !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey

const SidebarLink = ({
  href,
  active,
  children,
  variant = 'main',
  className
}: {
  href: string
  active: boolean
  children: ReactNode
  variant?: 'main' | 'sub'
  className?: string
}) => {
  const onNavigate = useContext(navigateContext)

  return (
    <Link
      href={href}
      data-active={active}
      onClick={(event) => {
        if (isPlainClick(event)) onNavigate(href)
      }}
      className={cn(
        'group/item relative flex items-center justify-between gap-2 rounded-lg outline-hidden focus-visible:ring-1 focus-visible:ring-o-line-strong',
        variant === 'main' && 'px-2.5 py-2 text-sm text-o-slate hover:text-o-ink',
        variant === 'sub' && 'h-7 ps-[38px] pe-3 text-[13.5px] text-o-muted hover:text-o-ink',
        active && 'bg-o-nav-active font-medium text-o-ink shadow-o-nav',
        className
      )}
    >
      {children}
    </Link>
  )
}

// Chromium ignores var() inside a <line> attribute, so the connector length is written into y2 directly.
const SubmenuArrow = ({ lineTo }: { lineTo: number }) => (
  <svg
    viewBox="0 0 16 11"
    fill="none"
    overflow="visible"
    aria-hidden
    className="absolute start-[15px] top-1/2 w-4 -translate-y-[calc(50%+1px)] text-o-muted rtl:-scale-x-100"
  >
    <path
      d="M13.3536 7.31378C13.5488 7.11852 13.5488 6.80194 13.3536 6.60667L10.1716 3.42469C9.97631 3.22943 9.65973 3.22943 9.46447 3.42469C9.2692 3.61996 9.2692 3.93654 9.46447 4.1318L12.2929 6.96023L9.46447 9.78865C9.2692 9.98392 9.2692 10.3005 9.46447 10.4958C9.65973 10.691 9.97631 10.691 10.1716 10.4958L13.3536 7.31378ZM0.5 0H0V3.46023C0 5.66943 1.7908 7.46023 4 7.46023H13V6.96023V6.46023H4C2.3431 6.46023 1 5.11713 1 3.46023V0H0.5Z"
      fill="currentColor"
    />
    <line x1="0.5" y1="0" x2="0.5" y2={lineTo} stroke="currentColor" strokeWidth="1" />
  </svg>
)

const SUB_ROW = 28
const SUB_GAP = 2

const NavGroup = ({ item, currentPath, count }: { item: SidebarItem; currentPath: string; count?: number | string }) => {
  const children = item.children ?? []
  const activeIndex = children.findIndex((child) => isNavActive(currentPath, child.href, child.exact))
  const open = children.length > 0 && (activeIndex !== -1 || isNavActive(currentPath, item.href))
  const selfActive = !open && isNavActive(currentPath, item.href, item.exact)

  return (
    <div className="flex flex-col">
      <SidebarLink href={children[0]?.href ?? item.href} active={selfActive} className={open ? 'font-medium text-o-ink' : undefined}>
        <span>{item.label}</span>
        {count !== undefined && <span className="text-[12.5px] font-normal text-o-faint">{count}</span>}
      </SidebarLink>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: [0.215, 0.61, 0.355, 1] }}
            className="flex flex-col gap-0.5 pt-0.5 pb-1"
          >
            {children.map((child: SidebarChild, index) => {
              const active = index === activeIndex
              return (
                <SidebarLink key={child.href} href={child.href} active={active} variant="sub">
                  {active && <SubmenuArrow lineTo={-(index * (SUB_ROW + SUB_GAP) + 13)} />}
                  <span>{child.label}</span>
                </SidebarLink>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export const EnvironmentSwitch = ({ size = 'md' }: { size?: 'md' | 'lg' }) => {
  const router = useRouter()
  const { pathname, teamSlug, envKind } = useAppRoute()

  return (
    <div className={cn('flex bg-o-track p-[3px]', size === 'lg' ? 'rounded-xl' : 'rounded-[10px]')} role="radiogroup" aria-label="Environment">
      {ENVIRONMENT_KINDS.map((kind) => {
        const active = kind === envKind
        return (
          <button
            key={kind}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => switchEnvironment(router, pathname, teamSlug, kind)}
            className={cn(
              'flex flex-1 items-center justify-center text-o-muted outline-hidden hover:text-o-ink',
              size === 'md' && 'gap-2 rounded-lg py-1.5 text-[13.5px]',
              size === 'lg' && 'gap-[7px] rounded-[10px] py-2.5 text-[14px] font-medium leading-[18px]',
              active && 'bg-o-env-active text-o-ink shadow-o-nav'
            )}
          >
            <span className={cn(size === 'lg' ? 'size-[7px] rounded-xs' : 'size-1.5 rounded-full', kind === 'production' ? 'bg-o-mint' : 'bg-o-amber')} />
            {ENVIRONMENT_LABELS[kind]}
          </button>
        )
      })}
    </div>
  )
}

type OrganizationSummary = ReturnType<typeof useOrganizations>['organizations'][number]

const organizationTone = (organization: OrganizationSummary | undefined) => organizationStatusMeta(organization?.status).tone

const OrganizationMark = ({ name, tone }: { name: string; tone: 'live' | 'pending' | 'failed' | 'idle' }) => (
  <span className="relative flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-o-avatar text-[13px] font-semibold text-o-ink">
    {name.slice(0, 1).toUpperCase()}
    <StatusDot tone={tone} className="absolute -end-[3px] -bottom-[3px] size-2.5 border-2 border-o-card" />
  </span>
)

const OrganizationSwitcher = ({ tenant }: { tenant: string }) => {
  const router = useRouter()
  const { scope, envPath, pathname } = useAppRoute()
  const { organizations } = useOrganizations(scope)
  const current = organizations.find((organization) => organization.tenant === tenant)
  const name = current?.label ?? tenant
  const section = pathname.split(`/organizations/${encodeURIComponent(tenant)}`)[1] ?? ''

  const options = organizations.map((organization) => ({
    value: organization.tenant,
    keywords: [organization.tenant, ...organization.domains],
    label: (
      <span className="flex min-w-0 items-center gap-2.5">
        <StatusDot tone={organizationTone(organization)} />
        <span className="truncate">{organization.label}</span>
      </span>
    )
  }))

  return (
    <Combobox
      allowSearch
      options={options}
      value={tenant}
      onChange={(next) => router.push(organizationPath(envPath, next, section))}
      searchPlaceholder="Search organizations"
      noOptionsLabel="No organizations found"
      menu={{ className: 'w-[228px]' }}
    >
      <button
        type="button"
        aria-label="Switch organization"
        className="flex w-full items-center gap-2.5 rounded-[10px] border border-o-line bg-o-card px-2.5 py-2 text-start outline-hidden hover:border-o-line-strong"
      >
        <OrganizationMark name={name} tone={organizationTone(current)} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium text-o-ink">{name}</span>
          <span className="truncate text-xs text-o-faint">{current?.domains[0] ?? tenant}</span>
        </span>
        <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden className="shrink-0 text-o-faint">
          <path d="M3.5 4.5L6 2L8.5 4.5M3.5 7.5L6 10L8.5 7.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </Combobox>
  )
}

const OrganizationNav = ({ tenant, groups, currentPath }: { tenant: string; groups: SidebarChild[][]; currentPath: string }) => {
  const { envPath } = useAppRoute()

  return (
    <nav className="flex flex-col gap-px" aria-label="Organization">
      <SidebarLink href={envPath('/organizations')} active={false} className="justify-start gap-1.5 py-[7px] text-[13.5px] text-o-muted">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path d="M8.5 3.5L5 7L8.5 10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        All organizations
      </SidebarLink>
      <OrganizationSwitcher tenant={tenant} />
      <div className="h-2.5" />
      {groups.map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {groupIndex > 0 && <div className="mx-2.5 my-2 h-px bg-o-line" />}
          {group.map((item) => (
            <SidebarLink key={item.href} href={item.href} active={isNavActive(currentPath, item.href, item.exact)}>
              <span>{item.label}</span>
            </SidebarLink>
          ))}
        </Fragment>
      ))}
    </nav>
  )
}

const workosLeft = (workos: { total: number; switched: number }) => {
  if (workos.total === 0) return undefined
  const left = workos.total - workos.switched
  return left > 0 ? `${left} left` : 'Done'
}

const MainNav = ({ items, currentPath }: { items: SidebarItem[]; currentPath: string }) => {
  const { scope, envPath } = useAppRoute()
  const { organizations } = useOrganizations(scope)
  const { workos } = useWorkosSummary(scope)
  const countFor = (href: string) => {
    if (href === envPath('/organizations') && organizations.length > 0) return organizations.length
    if (href === envPath('/workos') && workos) return workosLeft(workos)
    return undefined
  }

  return (
    <nav className="flex flex-col gap-px" aria-label="Main">
      {items.map((item) => (
        <NavGroup key={item.label} item={item} currentPath={currentPath} count={countFor(item.href)} />
      ))}
    </nav>
  )
}

const Sidebar = () => {
  const { mainItems, organizationGroups, tenant, currentPath: pathname } = useSidebarNav()
  const [optimistic, setOptimistic] = useState<{ href: string; from: string } | null>(null)
  const currentPath = optimistic && optimistic.from === pathname ? optimistic.href : pathname
  const navigate = useCallback((href: string) => setOptimistic({ href, from: pathname }), [pathname])

  return (
    <navigateContext.Provider value={navigate}>
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col justify-between px-3.5 py-[18px] md:flex">
        <div className="flex min-h-0 flex-col gap-5">
          <TeamMenu />
          <EnvironmentSwitch />
          <div className="hide-scrollbar min-h-0 overflow-y-auto">
            {tenant && organizationGroups ? (
              <OrganizationNav tenant={tenant} groups={organizationGroups} currentPath={currentPath} />
            ) : (
              <MainNav items={mainItems} currentPath={currentPath} />
            )}
          </div>
        </div>
        <UserMenu />
      </aside>
    </navigateContext.Provider>
  )
}

export default Sidebar
